import { ScaleElement } from '@scale/base-element';
import { Events, FiltersUpdatedEvent } from '@scale/events';
import { shake } from '@scale/utilities';

class ScaleFacets extends ScaleElement {
  setup() {
    window.addEventListener('popstate', () => this.#onPopState(), { signal: this.signal });
    this.onDocument(Events.FILTERS_FAILED, () => this.#restore());
  }

  handleFilterChange(event) {
    this.#mirror(event.target);
    this.debounce('filter', () => this.#apply(), 50);
  }

  handleSortChange(event) {
    this.#mirror(event.target);
    this.#apply();
    this.closeDrawer();
  }

  handleClearFilter(event) {
    event.preventDefault();
    const pill = event.target.closest('a');
    this.#fold(pill);
    this.#navigate(pill.href);
  }

  handleClearAll(event) {
    event.preventDefault();
    const link = event.target.closest('a');
    const row = link.closest('[data-ref="active-pills"]');
    if (row) this.#fold(row);
    this.#navigate(link.href);
  }

  openDrawer() {
    const drawer = document.getElementById('filter-drawer');
    if (drawer) drawer.open();
  }

  closeDrawer() {
    const drawer = document.getElementById('filter-drawer');
    if (drawer) drawer.close();
  }

  #apply() {
    const url = this.#buildURL();
    history.pushState({}, '', url);
    this.broadcast(new FiltersUpdatedEvent(url));
  }

  #navigate(url) {
    history.pushState({}, '', url);
    this.broadcast(new FiltersUpdatedEvent(url));
  }

  #fold(element) {
    if (element.contains(document.activeElement)) {
      let next = element.nextElementSibling;
      while (next.classList.contains('is-leaving')) next = next.nextElementSibling;
      (next.querySelector('summary') ?? next).focus();
    }
    element.classList.add('is-leaving');
  }

  #restore() {
    for (const element of this.querySelectorAll('.is-leaving')) element.classList.remove('is-leaving');
    const form = this.ref('form');
    const shown = [...form.elements].map((control) => [control, control.checked, control.value]);
    form.reset();
    for (const [control, checked, value] of shown) {
      if (control.checked !== checked || control.value !== value) shake(control.closest('label') ?? control);
    }
  }

  #onPopState() {
    this.closeDrawer();
    this.broadcast(new FiltersUpdatedEvent(window.location.href));
  }

  #mirror(control) {
    for (const copy of this.ref('form').elements) {
      if (copy === control || copy.name !== control.name) continue;
      if (control.type === 'checkbox') {
        if (copy.value === control.value) copy.checked = control.checked;
      } else {
        copy.value = control.value;
      }
    }
  }

  #buildURL() {
    const form = this.ref('form');
    if (!form) return window.location.href;

    const formData = new FormData(form);
    const params = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      if (value === '' || params.has(key, value)) continue;
      params.append(key, value);
    }

    params.delete('page');
    const url = new URL(window.location.pathname, window.location.origin);
    url.search = params.toString();
    return url.toString();
  }
}

if (!customElements.get('scale-facets')) {
  customElements.define('scale-facets', ScaleFacets);
}
